import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Plus, X } from 'lucide-react'
import shell from './AiActionPreview.module.scss'
import styles from './PayActionCard.module.scss'
import {
    normalizePay,
    parsePayGroup,
    payRows,
    type PartialPay,
    type PayGroup,
} from 'utils/payActionPrompt'
import CommonEditor from 'components/shared/CommonEditor'
import { getText, type DeltaJson } from 'lib/value'
import {
    bsvAmountToSats,
    convertBsvAmountInputUnit,
    normalizeDecimalAmount,
} from 'utils/bsvAmountInput'

export interface PayCardChange {
    payments: { recipient: string; amount: number | null; unit: 'sats' | 'bsv' }[]
}

interface PayActionCardProps {
    settingsPay: PayGroup | PartialPay | null
    promptContent: string
    onChange: (next: PayCardChange) => void
    onRemove: () => void
}

type Row = { id: number; recipient: string; amount: string; unit: 'sats' | 'bsv' }

/** Keep typed decimal strings locally; commit the complete payment group atomically. */
export default function PayActionCard({
    settingsPay,
    promptContent,
    onChange,
    onRemove,
}: PayActionCardProps) {
    const nextId = useRef(0)
    const source = payRows(settingsPay || parsePayGroup(promptContent))
    const makeRows = (payments: PartialPay[]): Row[] =>
        payments.map(pay => ({
            id: nextId.current++,
            recipient: pay.recipient,
            amount: pay.amount == null ? '' : normalizeDecimalAmount(pay.amount),
            unit: pay.unit,
        }))
    const [rows, setRows] = useState(() => makeRows(source))
    const rowsRef = useRef(rows)
    const editingRef = useRef(false)
    const pendingKey = useRef<string | null>(null)
    const rowFields = (values: Row[]) =>
        values.map(row => ({
            recipient: row.recipient,
            amount: bsvAmountToSats(row.amount, row.unit) === '' ? null : Number(row.amount),
            unit: row.unit,
        }))
    const sourceKey = JSON.stringify(source)

    useEffect(() => {
        if (pendingKey.current != null) {
            if (sourceKey === pendingKey.current) pendingKey.current = null
            return
        }
        if (editingRef.current) return
        if (sourceKey === JSON.stringify(rowFields(rowsRef.current).map(normalizePay))) return
        const next = makeRows(source)
        rowsRef.current = next
        setRows(next)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceKey])

    const update = (next: Row[]) => {
        rowsRef.current = next
        setRows(next)
        const payments = rowFields(next)
        pendingKey.current = JSON.stringify(payments.map(normalizePay))
        onChange({ payments })
    }
    const updateRow = (id: number, patch: Partial<Row>) => {
        update(rowsRef.current.map(row => (row.id === id ? { ...row, ...patch } : row)))
    }
    const toggleUnit = (id: number) => {
        const row = rowsRef.current.find(row => row.id === id)!
        const unit = row.unit === 'sats' ? 'bsv' : 'sats'
        const amount = convertBsvAmountInputUnit(row.amount, row.unit, unit)
        // Invalid input must remain invalid rather than being reinterpreted in another unit.
        if (row.amount && amount === '') return
        updateRow(id, { unit, amount })
    }
    const amounts = rows.map(row => bsvAmountToSats(row.amount, row.unit))
    const total = amounts.every(amount => amount !== '' && amount > 0)
        ? amounts.reduce<number>((sum, amount) => sum + Number(amount), 0)
        : null

    return (
        <div
            className={cn(shell.toolActionBadge, shell.fullWidthActionBadge, styles.payCard)}
            data-action-tag="pay"
        >
            <div className={shell.aiEditorPreviewMain}>
                <div className={shell.aiEditorPreviewTopRow}>
                    <div className={shell.aiEditorPreviewContentButton}>
                        <span className={shell.aiEditorPreviewText}>
                            <span className={shell.headerTitle}>
                                <span className={shell.headerTitleText}>send payment</span>
                                {total != null && (
                                    <span className={shell.headerPrice}>
                                        {total.toLocaleString()} sats
                                    </span>
                                )}
                            </span>
                        </span>
                    </div>
                    <span className={shell.headerActions}>
                        <button
                            type="button"
                            className={styles.removeButton}
                            onClick={onRemove}
                            aria-label="Remove payment"
                        >
                            <X size={14} />
                        </button>
                    </span>
                </div>
                {rows.map((row, index) => (
                    <div
                        key={row.id}
                        className={styles.recipientRow}
                        data-testid="pay-card-row"
                        onFocusCapture={() => {
                            editingRef.current = true
                        }}
                        onBlurCapture={() => {
                            editingRef.current = false
                        }}
                    >
                        <div className={styles.payRow}>
                            <span className={styles.payLabel}>To</span>
                            <div
                                className={styles.payRecipientEditor}
                                data-testid="pay-card-recipient"
                            >
                                <CommonEditor
                                    defaultValue={{
                                        ops: [{ insert: row.recipient ? `@${row.recipient}` : '' }],
                                    }}
                                    placeholder="@username or address"
                                    onChange={(value: DeltaJson) =>
                                        updateRow(row.id, {
                                            recipient: getText(value).trim().replace(/^@+/, ''),
                                        })
                                    }
                                    type="address-input"
                                    editorTestId="pay-card-recipient-editor"
                                    userMentionsOnly
                                />
                            </div>
                            {rows.length > 1 && (
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    aria-label={`Remove recipient ${index + 1}`}
                                    onClick={() =>
                                        update(rowsRef.current.filter(item => item.id !== row.id))
                                    }
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <div className={styles.payRow}>
                            <span className={styles.payLabel}>Amount</span>
                            <input
                                className={styles.payInput}
                                type="text"
                                inputMode="decimal"
                                value={row.amount}
                                placeholder={row.unit === 'bsv' ? '0.00000000' : '0'}
                                onChange={event =>
                                    updateRow(row.id, { amount: event.target.value })
                                }
                                aria-label={`Amount for recipient ${index + 1}`}
                                data-testid="pay-card-amount"
                            />
                            <button
                                type="button"
                                className={styles.unitToggle}
                                onClick={() => toggleUnit(row.id)}
                                data-testid="pay-card-unit"
                            >
                                {row.unit === 'bsv' ? 'BSV' : 'sats'}
                            </button>
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    className={styles.addRecipient}
                    onClick={() =>
                        update([
                            ...rowsRef.current,
                            {
                                id: nextId.current++,
                                recipient: '',
                                amount: '',
                                unit: rowsRef.current[rowsRef.current.length - 1]?.unit || 'sats',
                            },
                        ])
                    }
                >
                    <Plus size={14} /> Add recipient
                </button>
            </div>
        </div>
    )
}
